import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, User, Lock, Trash2, LogOut, Download, FileText, Shield, Bell, Moon, Sun, Smartphone, X, Check, Info, Crown, Star, BookOpen, Mic, Activity, Wind, Calendar, BarChart3, Dumbbell, Trophy, Heart, Wallet, CreditCard, Clock, ScanFace } from 'lucide-react';
import axios from 'axios';
import { APP_VERSION } from '@/App';
import { isWebAuthnSupported, registerBiometrics, BIOMETRIC_EMAIL_KEY } from '@/lib/webauthn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAVORITE_TOOLS = [
  { id: 'journal', label: 'Dagbok', icon: BookOpen },
  { id: 'voice', label: 'Röstinspelning', icon: Mic },
  { id: 'mood', label: 'Humör', icon: Activity },
  { id: 'exercises', label: 'Andning', icon: Wind },
  { id: 'planner', label: 'Veckoplanering', icon: Calendar },
  { id: 'stats', label: 'Statistik', icon: BarChart3 },
  { id: 'fitness', label: 'Träning', icon: Dumbbell },
  { id: 'goals', label: 'Mål', icon: Trophy },
  { id: 'habits', label: 'Vanor', icon: Heart },
  { id: 'notes', label: 'Anteckningar', icon: BookOpen },
  { id: 'finance', label: 'Ekonomi', icon: Wallet },
];

// Password strength checker
const checkPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    hasLetter: /[A-Za-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  return { checks, strength };
};

// Daily inspiration messages
const DAILY_MESSAGES = [
  "Ta en stund för dig själv idag. Du förtjänar det.",
  "Hur mår du just nu? Din dagbok väntar på dig.",
  "Påminnelse: Det är okej att känna det du känner.",
  "En djup andning kan göra stor skillnad. Prova en övning idag.",
  "Du är starkare än du tror. Varje steg räknas.",
  "Idag är en ny dag med nya möjligheter att vara snäll mot dig själv.",
  "Ta dig tid att reflektera. Dina tankar är värdefulla.",
  "Kom ihåg: Du behöver inte vara perfekt, bara närvarande."
];

export default function SettingsPage() {
  const { user, token, logout, has2FAEnabled, refreshUser, isPremium, isTrial, trialDaysLeft, autoLogoutMinutes, setAutoLogoutMinutes } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Sync local form state when user loads/changes from AuthContext
  useEffect(() => {
    if (user?.name) setName(user.name);
    if (user?.email) setEmail(user.email);
  }, [user?.name, user?.email]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [darkMode, setDarkMode] = useState(false);
  
  // Subscription state
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  // Referral state
  const [referral, setReferral] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);


  
  // Security state
  const [twoFAEnabled, setTwoFAEnabled] = useState(has2FAEnabled);
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [disabling2FAPassword, setDisabling2FAPassword] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [biometricEnabledHere, setBiometricEnabledHere] = useState(!!localStorage.getItem(BIOMETRIC_EMAIL_KEY));
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [userFavorites, setUserFavorites] = useState([]);

  const passwordStrength = checkPasswordStrength(newPassword);

  // Swedish only
  const c = {
    back: 'Tillbaka',
    title: 'Kontoinställningar',
    reminders: 'Påminnelser',
    remindersDesc: 'Få en mjuk daglig påminnelse om att ta en stund för dig själv och reflektera.',
    dailyReminders: 'Dagliga påminnelser',
    notificationsBlocked: 'Notiser blockerade i webbläsaren',
    notificationsTime: 'Skickas mellan kl 09:00-10:00',
    notificationsBlockedInfo: 'Du har blockerat notiser. Gå till webbläsarens inställningar för att tillåta dem.',
    changeName: 'Ändra namn',
    name: 'Namn',
    saveName: 'Spara namn',
    changePassword: 'Ändra lösenord',
    currentPassword: 'Nuvarande lösenord',
    newPassword: 'Nytt lösenord',
    confirmNewPassword: 'Bekräfta nytt lösenord',
    updatePassword: 'Uppdatera lösenord',
    appearance: 'Utseende',
    darkMode: 'Mörkt läge',
    darkModeDesc: 'Mildare för ögonen i mörker',
    dataPrivacy: 'Data & Integritet',
    exportJson: 'Ladda ner som JSON',
    exportPdf: 'Ladda ner som PDF',
    privacyPolicy: 'Integritetspolicy',
    termsOfService: 'Användarvillkor',
    deleteAccount: 'Radera konto',
    deleteConfirmText: 'Är du helt säker på att du vill radera ditt konto? All din data kommer att raderas permanent.',
    cancel: 'Avbryt',
    deleteForever: 'Ja, radera mitt konto',
    logout: 'Logga ut',
    darkModeOn: 'Mörkt läge aktiverat',
    darkModeOff: 'Ljust läge aktiverat',
    nameUpdated: 'Namn uppdaterat',
    nameError: 'Kunde inte uppdatera namn',
    passwordMismatch: 'Lösenorden matchar inte',
    passwordTooShort: 'Lösenordet måste vara minst 6 tecken',
    passwordUpdated: 'Lösenord uppdaterat',
    passwordError: 'Kunde inte uppdatera lösenord',
    dataDownloaded: 'Din data har laddats ner',
    dataError: 'Kunde inte exportera data',
    accountDeleted: 'Kontot har raderats',
    accountDeleteError: 'Kunde inte radera kontot',
    notificationsEnabled: 'Dagliga påminnelser aktiverade',
    notificationsDisabled: 'Påminnelser avaktiverade'
  };

  useEffect(() => {
    // Check notification permission and stored preference
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      const stored = localStorage.getItem('notifications-enabled');
      setNotificationsEnabled(stored === 'true' && Notification.permission === 'granted');
    }
    
    // Check dark mode preference
    const storedDarkMode = localStorage.getItem('dark-mode') === 'true';
    setDarkMode(storedDarkMode);
    if (storedDarkMode) {
      document.documentElement.classList.add('dark');
    }
    
    // Load user favorites
    const loadFavs = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserFavorites(res.data.favorites || []);
      } catch (e) {}
    };
    if (token) loadFavs();

    // Load subscription data
    const loadSubscription = async () => {
      try {
        const res = await axios.get(`${API}/subscription/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionData(res.data);
      } catch (e) {
        console.error('Could not load subscription data');
      } finally {
        setLoadingSubscription(false);
      }
    };
    if (token) loadSubscription();

    // Load referral code
    const loadReferral = async () => {
      try {
        const res = await axios.get(`${API}/referral/my-code`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferral(res.data);
      } catch (e) {
        console.error('Could not load referral code');
      } finally {
        setLoadingReferral(false);
      }
    };
    if (token) loadReferral();
  }, []);

  const shareUrl = referral
    ? `${window.location.origin}${referral.share_path}`
    : '';

  const handleCopyReferral = async () => {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedCode(true);
      toast.success('Länken är kopierad — klistra in den var du vill!');
      setTimeout(() => setCopiedCode(false), 2500);
    } catch (e) {
      toast.error('Kunde inte kopiera. Markera texten manuellt.');
    }
  };

  const handleShareReferral = async () => {
    if (!referral) return;
    const shareData = {
      title: 'Livon — full tillgång',
      text: `Kolla in Livon! Använd min kod ${referral.code} och få ${referral.discount_amount} kr rabatt (${referral.final_price} kr istället för ${referral.original_price} kr).`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // user cancelled — no-op
      }
    } else {
      handleCopyReferral();
    }
  };

  const handleToggleDarkMode = (enabled) => {
    setDarkMode(enabled);
    localStorage.setItem('dark-mode', enabled.toString());
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    toast.success(enabled ? c.darkModeOn : c.darkModeOff);
  };

  const handleToggleFavorite = async (toolId) => {
    const updated = userFavorites.includes(toolId) 
      ? userFavorites.filter(f => f !== toolId)
      : [...userFavorites, toolId];
    setUserFavorites(updated);
    try {
      await axios.post(`${API}/dashboard/favorites`, { favorites: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(updated.includes(toolId) ? 'Tillagd som favorit' : 'Borttagen från favoriter');
    } catch (e) {
      toast.error('Kunde inte spara favoriter');
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/auth/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Create PDF-friendly HTML content
      const pdfContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Livon - Min Data</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1a1a2e; }
            h1 { color: #2d6a7a; border-bottom: 2px solid #2d6a7a; padding-bottom: 10px; }
            h2 { color: #2d6a7a; margin-top: 30px; }
            .section { margin-bottom: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px; }
            .entry { margin-bottom: 15px; padding: 15px; background: white; border-radius: 6px; border-left: 3px solid #2d6a7a; }
            .date { color: #666; font-size: 0.9em; }
            .content { margin-top: 8px; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 0.8em; }
          </style>
        </head>
        <body>
          <h1>🦋 Livon - Min Data</h1>
          <p><strong>Exporterad:</strong> ${new Date().toLocaleDateString('sv-SE')}</p>
          <p><strong>Användare:</strong> ${data.user?.name || 'Anonym'} (${data.user?.email || ''})</p>
          
          ${data.journal_entries?.length > 0 ? `
          <div class="section">
            <h2>📓 Dagboksinlägg (${data.journal_entries.length} st)</h2>
            ${data.journal_entries.map(entry => `
              <div class="entry">
                <div class="date">${new Date(entry.created_at).toLocaleDateString('sv-SE')}</div>
                <div class="content">${entry.content?.substring(0, 500) || ''}${entry.content?.length > 500 ? '...' : ''}</div>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          ${data.reflections?.length > 0 ? `
          <div class="section">
            <h2>💭 Reflektioner (${data.reflections.length} st)</h2>
            ${data.reflections.map(r => `
              <div class="entry">
                <div class="date">${new Date(r.created_at).toLocaleDateString('sv-SE')} - ${r.type || 'Reflektion'}</div>
                <div class="content">${JSON.stringify(r.answers || r.content || {})}</div>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          ${data.gratitude_entries?.length > 0 ? `
          <div class="section">
            <h2>🙏 Tacksamhet (${data.gratitude_entries.length} st)</h2>
            ${data.gratitude_entries.map(g => `
              <div class="entry">
                <div class="date">${new Date(g.created_at).toLocaleDateString('sv-SE')}</div>
                <div class="content">${g.items?.join(', ') || ''}</div>
              </div>
            `).join('')}
          </div>
          ` : ''}
          
          <div class="footer">
            <p>Genererad av Livon - Din kompletta välmåendeplattform</p>
            <p>© ${new Date().getFullYear()} Livon</p>
          </div>
        </body>
        </html>
      `;
      
      // Open print dialog for PDF
      const printWindow = window.open('', '_blank');
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Small delay to ensure content is loaded
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success('PDF redo att skrivas ut/sparas');
    } catch (error) {
      toast.error('Kunde inte exportera till PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleToggleNotifications = async (enabled) => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error('Din webbläsare stödjer inte notiser');
      return;
    }

    if (enabled) {
      try {
        const { subscribeToPush } = await import('@/utils/pushNotifications');
        const result = await subscribeToPush(token);
        if (result.success) {
          setNotificationsEnabled(true);
          setNotificationPermission('granted');
          localStorage.setItem('notifications-enabled', 'true');
          toast.success('Push-notifikationer aktiverade');
        } else if (result.reason === 'denied') {
          setNotificationPermission('denied');
          toast.error('Du behöver tillåta notiser i webbläsaren');
        } else {
          toast.error('Kunde inte aktivera notiser');
        }
      } catch {
        toast.error('Något gick fel vid aktivering av notiser');
      }
    } else {
      try {
        const { unsubscribeFromPush } = await import('@/utils/pushNotifications');
        await unsubscribeFromPush(token);
      } catch { /* ignore */ }
      setNotificationsEnabled(false);
      localStorage.setItem('notifications-enabled', 'false');
      toast.success('Push-notifikationer avaktiverade');
    }
  };

  const scheduleDailyNotification = () => {
    // Schedule notification check every hour
    // The actual notification will only show once per day
    const lastNotification = localStorage.getItem('last-notification-date');
    const today = new Date().toDateString();
    
    if (lastNotification !== today && notificationsEnabled) {
      const hour = new Date().getHours();
      // Send notification between 9 AM and 10 AM
      if (hour >= 9 && hour <= 10) {
        const message = DAILY_MESSAGES[Math.floor(Math.random() * DAILY_MESSAGES.length)];
        new Notification('Livon', {
          body: message,
          icon: '/icons/icon.svg',
          tag: 'daily-reminder'
        });
        localStorage.setItem('last-notification-date', today);
      }
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Namnet kan inte vara tomt');
      return;
    }
    if (trimmed === user?.name) {
      toast.info('Namnet är oförändrat');
      return;
    }

    setSavingName(true);
    try {
      await axios.put(`${API}/auth/profile`, { name: trimmed }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Namnet har uppdaterats');
      // Refresh AuthContext rather than full reload
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || c.nameError;
      toast.error(msg);
    } finally {
      setSavingName(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed) {
      toast.error('E-postadressen kan inte vara tom');
      return;
    }
    if (trimmed === (user?.email || '').toLowerCase()) {
      toast.info('E-postadressen är oförändrad');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Ange en giltig e-postadress');
      return;
    }
    if (!emailPassword) {
      toast.error('Bekräfta med ditt nuvarande lösenord');
      return;
    }

    setSavingEmail(true);
    try {
      await axios.put(`${API}/auth/email`, {
        new_email: trimmed,
        current_password: emailPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('E-postadressen har uppdaterats');
      setEmailPassword('');
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Kunde inte uppdatera e-postadressen';
      toast.error(msg);
    } finally {
      setSavingEmail(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(c.passwordMismatch);
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(c.passwordTooShort);
      return;
    }
    
    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(c.passwordUpdated);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || c.passwordError;
      toast.error(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/auth/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `livon-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(c.dataDownloaded);
    } catch (error) {
      toast.error(c.dataError);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(c.accountDeleted);
      logout();
      navigate('/');
    } catch (error) {
      toast.error(c.accountDeleteError);
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 2FA Functions
  const handleEnable2FA = async () => {
    setEnabling2FA(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShow2FAVerify(true);
      toast.info('Verifieringskod skickad till din e-post');
    } catch (error) {
      toast.error('Kunde inte aktivera 2FA');
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleVerify2FAEnable = async () => {
    setEnabling2FA(true);
    try {
      await axios.post(`${API}/auth/2fa/verify-enable`, { code: twoFACode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFAEnabled(true);
      setShow2FAVerify(false);
      setTwoFACode('');
      refreshUser();
      toast.success('Tvåfaktorsautentisering aktiverad!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ogiltig kod');
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleEnableBiometrics = async () => {
    setBiometricLoading(true);
    try {
      await registerBiometrics(token);
      localStorage.setItem(BIOMETRIC_EMAIL_KEY, user.email);
      setBiometricEnabledHere(true);
      toast.success('Face ID / Touch ID aktiverat på denna enhet');
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        toast.error('Registreringen avbröts');
      } else {
        toast.error(error?.message || 'Kunde inte aktivera Face ID / Touch ID');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDisableBiometrics = async () => {
    setBiometricLoading(true);
    try {
      await axios.delete(`${API}/webauthn/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.removeItem(BIOMETRIC_EMAIL_KEY);
      setBiometricEnabledHere(false);
      toast.success('Face ID / Touch ID inaktiverat');
    } catch (error) {
      toast.error('Kunde inte inaktivera Face ID / Touch ID');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setEnabling2FA(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { 
        current_password: disabling2FAPassword,
        new_password: disabling2FAPassword 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFAEnabled(false);
      setShowDisable2FA(false);
      setDisabling2FAPassword('');
      refreshUser();
      toast.success('Tvåfaktorsautentisering inaktiverad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fel lösenord');
    } finally {
      setEnabling2FA(false);
    }
  };

  // Session Functions
  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await axios.get(`${API}/auth/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
      setShowSessions(true);
    } catch (error) {
      toast.error('Kunde inte hämta sessioner');
    } finally {
      setLoadingSessions(false);
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/auth/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session avslutad');
    } catch (error) {
      toast.error('Kunde inte avsluta session');
    }
  };

  const revokeAllSessions = async () => {
    try {
      await axios.post(`${API}/auth/sessions/revoke-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSessions();
      toast.success('Alla andra sessioner avslutade');
    } catch (error) {
      toast.error('Kunde inte avsluta sessioner');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background" data-testid="settings-page">
      {/* Header */}
      <header className="border-b border-border/30 bg-white/80 dark:bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {c.back}
          </Button>
          <h1 className="font-heading text-xl font-semibold">{c.title}</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        {/* Profile section */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
              <User className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-semibold">{user?.name}</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Mitt köp */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.03s' }} data-testid="subscription-section">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-violet-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Mitt köp</h3>
          </div>

          {loadingSubscription ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Current Status */}
              <div className="rounded-xl bg-secondary/30 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {isPremium ? (
                    <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full flex items-center gap-1.5" data-testid="subscription-status-active">
                      <Crown className="w-3.5 h-3.5" /> Premium aktiv
                    </span>
                  ) : isTrial ? (
                    <span className="text-sm font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full flex items-center gap-1.5" data-testid="subscription-status-trial">
                      <Clock className="w-3.5 h-3.5" /> Provperiod ({trialDaysLeft} {trialDaysLeft === 1 ? 'dag' : 'dagar'} kvar)
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground bg-secondary px-3 py-1 rounded-full" data-testid="subscription-status-free">
                      Gratis
                    </span>
                  )}
                </div>

                {subscriptionData?.subscription?.subscription_type && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Plantyp</span>
                    <span className="text-sm font-medium" data-testid="subscription-plan-type">
                      Full tillgång (299 kr — betald)
                    </span>
                  </div>
                )}

                {subscriptionData?.subscription?.subscription_type === 'lifetime' && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Giltighet</span>
                    <span className="text-sm font-medium" data-testid="subscription-lifetime-label">
                      Köpt — använd när du vill
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                {!isPremium && !isTrial && (
                  <Button
                    onClick={() => navigate('/subscription')}
                    className="rounded-full"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Köp Livon — 299 kr
                  </Button>
                )}
                {!isPremium && isTrial && (
                  <Button
                    onClick={() => navigate('/subscription')}
                    className="rounded-full"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Köp Livon — 299 kr
                  </Button>
                )}
              </div>

              {/* Payment History */}
              {subscriptionData?.payments?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground">Betalningshistorik</h4>
                  <div className="space-y-2" data-testid="payment-history-list">
                    {subscriptionData.payments.slice(0, 10).map((payment, index) => (
                      <div key={payment.id || index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20" data-testid={`payment-item-${index}`}>
                        <div className="space-y-0.5">
                          <p className="text-sm font-medium">
                            {payment.subscription_type === 'lifetime' ? 'Full tillgång' : 'Betalning'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(payment.created_at).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                        <div className="text-right space-y-0.5">
                          <p className="text-sm font-medium">{payment.amount} kr</p>
                          <p className={`text-xs ${payment.payment_status === 'paid' ? 'text-green-600' : payment.payment_status === 'pending' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                            {payment.payment_status === 'paid' ? 'Betald' :
                             payment.payment_status === 'pending' ? 'Väntar' :
                             payment.payment_status === 'expired' ? 'Utgången' :
                             payment.payment_status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Dela Livon — Referral */}
        <div className="bg-white rounded-2xl p-8 space-y-5 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.04s' }} data-testid="referral-section">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold">Dela Livon med en vän</h3>
              <p className="text-sm text-muted-foreground">Din vän får 20 kr rabatt — 279 kr istället för 299 kr.</p>
            </div>
          </div>

          {loadingReferral ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : referral ? (
            <div className="space-y-4">
              {/* Referral code display */}
              <div className="rounded-xl border-2 border-dashed border-rose-200 bg-rose-50/50 p-5 text-center">
                <p className="text-xs uppercase tracking-wider text-rose-700/70 mb-2">Din personliga kod</p>
                <p className="font-heading text-3xl font-semibold text-rose-900 tracking-wider" data-testid="referral-code">
                  {referral.code}
                </p>
              </div>

              {/* Share URL */}
              <div className="flex items-center gap-2 rounded-xl bg-secondary/30 p-3">
                <div className="flex-1 min-w-0 text-sm text-muted-foreground truncate" data-testid="referral-share-url">
                  {shareUrl}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyReferral}
                  className="rounded-full shrink-0"
                  data-testid="copy-referral-btn"
                >
                  {copiedCode ? (
                    <>
                      <Check className="w-4 h-4 mr-1" /> Kopierad
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-1" /> Kopiera
                    </>
                  )}
                </Button>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleShareReferral}
                  className="rounded-full bg-rose-500 hover:bg-rose-600 text-white"
                  data-testid="share-referral-btn"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Dela med en vän
                </Button>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
                <Star className="w-4 h-4 text-amber-500" />
                <span data-testid="referral-friends-count">
                  {referral.friends_invited === 0
                    ? 'Du har inte bjudit in någon vän än.'
                    : referral.friends_invited === 1
                    ? '1 vän har köpt Livon via din kod — tack!'
                    : `${referral.friends_invited} vänner har köpt Livon via din kod — tack!`}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Kunde inte ladda din kod. Försök igen senare.</p>
          )}
        </div>


        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.05s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-sky-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.reminders}</h3>
          </div>
          <p className="text-muted-foreground">
            {c.remindersDesc}
          </p>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium">{c.dailyReminders}</p>
              <p className="text-sm text-muted-foreground">
                {notificationPermission === 'denied' 
                  ? c.notificationsBlocked
                  : c.notificationsTime}
              </p>
            </div>
            <Switch
              checked={notificationsEnabled}
              onCheckedChange={handleToggleNotifications}
              disabled={notificationPermission === 'denied'}
              data-testid="notifications-switch"
            />
          </div>
          {notificationPermission === 'denied' && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              {c.notificationsBlockedInfo}
            </p>
          )}
        </div>

        {/* Update name */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.changeName}</h3>
          </div>
          
          <form onSubmit={handleUpdateName} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{c.name}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="name-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={savingName || name === user?.name}
              className="rounded-full"
              data-testid="save-name-btn"
            >
              {savingName ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {c.saveName}
            </Button>
          </form>
        </div>

        {/* Update email */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.18s' }} data-testid="email-section">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <User className="w-5 h-5 text-sky-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Ändra e-postadress</h3>
          </div>

          <form onSubmit={handleUpdateEmail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-postadress</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="email-input"
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailPassword">Bekräfta med ditt lösenord</Label>
              <Input
                id="emailPassword"
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="email-password-input"
                autoComplete="current-password"
              />
              <p className="text-xs text-muted-foreground">Av säkerhetsskäl behöver vi ditt nuvarande lösenord innan vi byter e-postadress.</p>
            </div>
            <Button
              type="submit"
              disabled={savingEmail || (email || '').trim().toLowerCase() === (user?.email || '').toLowerCase() || !emailPassword}
              className="rounded-full"
              data-testid="save-email-btn"
            >
              {savingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Spara e-postadress
            </Button>
          </form>
        </div>

        {/* Update password */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Lock className="w-5 h-5 text-amber-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.changePassword}</h3>
          </div>
          
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">{c.currentPassword}</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{c.newPassword}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{c.confirmNewPassword}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword || passwordStrength.strength < 3}
              className="rounded-full btn-warm"
              data-testid="save-password-btn"
            >
              {savingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {c.updatePassword}
            </Button>
            
            {/* Password strength indicator */}
            {newPassword && (
              <div className="space-y-2 pt-2">
                <div className="flex gap-1">
                  {[1, 2, 3].map((level) => (
                    <div 
                      key={level}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        passwordStrength.strength >= level 
                          ? level === 1 ? 'bg-red-400' 
                          : level === 2 ? 'bg-yellow-400' 
                          : 'bg-green-500'
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <div className="space-y-1 text-xs">
                  <div className={`flex items-center gap-2 ${passwordStrength.checks.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {passwordStrength.checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Minst 8 tecken
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.checks.hasLetter ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {passwordStrength.checks.hasLetter ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Innehåller bokstäver
                  </div>
                  <div className={`flex items-center gap-2 ${passwordStrength.checks.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {passwordStrength.checks.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Innehåller siffror
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Security - 2FA */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.25s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Säkerhet</h3>
          </div>
          
          {/* 2FA Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Tvåfaktorsautentisering (2FA)</p>
                <p className="text-sm text-muted-foreground">
                  Extra skydd med verifieringskod via e-post
                </p>
              </div>
              {twoFAEnabled ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                    <Check className="w-4 h-4" /> Aktiverad
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowDisable2FA(true)}
                    className="rounded-full text-destructive border-destructive/50"
                  >
                    Inaktivera
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleEnable2FA}
                  disabled={enabling2FA}
                  className="rounded-full"
                  data-testid="enable-2fa-btn"
                >
                  {enabling2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Aktivera
                </Button>
              )}
            </div>
            
            {/* 2FA Verification Input */}
            {show2FAVerify && (
              <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                <p className="text-sm">Ange koden som skickats till din e-post:</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-32 text-center tracking-widest font-mono"
                  />
                  <Button 
                    onClick={handleVerify2FAEnable}
                    disabled={enabling2FA || twoFACode.length !== 6}
                    className="rounded-full"
                  >
                    Verifiera
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => { setShow2FAVerify(false); setTwoFACode(''); }}
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            )}
            
            {/* Disable 2FA Confirmation */}
            {showDisable2FA && (
              <div className="bg-red-50 rounded-xl p-4 space-y-3">
                <p className="text-sm text-red-700">Ange ditt lösenord för att inaktivera 2FA:</p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={disabling2FAPassword}
                    onChange={(e) => setDisabling2FAPassword(e.target.value)}
                    placeholder="Ditt lösenord"
                    className="flex-1"
                  />
                  <Button 
                    variant="destructive"
                    onClick={handleDisable2FA}
                    disabled={enabling2FA || !disabling2FAPassword}
                    className="rounded-full"
                  >
                    Inaktivera
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => { setShowDisable2FA(false); setDisabling2FAPassword(''); }}
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Face ID / Touch ID Section */}
          {isWebAuthnSupported() && (
            <div className="pt-4 border-t border-border/30 space-y-3" data-testid="biometric-section">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-medium flex items-center gap-2">
                    <ScanFace className="w-4 h-4 text-primary" />
                    Face ID / Touch ID
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Logga in snabbt och säkert med biometri på denna enhet
                  </p>
                </div>
                {biometricEnabledHere ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" /> Aktiverad
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisableBiometrics}
                      disabled={biometricLoading}
                      className="rounded-full text-destructive border-destructive/50"
                      data-testid="disable-biometric-btn"
                    >
                      {biometricLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inaktivera'}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleEnableBiometrics}
                    disabled={biometricLoading}
                    className="rounded-full"
                    data-testid="enable-biometric-btn"
                  >
                    {biometricLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Aktivera
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Auto logout Section */}
          <div className="pt-4 border-t border-border/30 space-y-3" data-testid="auto-logout-section">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-medium">Automatisk utloggning</p>
                <p className="text-sm text-muted-foreground">
                  Logga ut automatiskt efter en tids inaktivitet — skyddar dina uppgifter om någon annan kommer åt din enhet
                </p>
              </div>
              <select
                value={autoLogoutMinutes}
                onChange={(e) => {
                  const minutes = parseInt(e.target.value, 10);
                  setAutoLogoutMinutes(minutes);
                  toast.success(minutes === 0 ? 'Automatisk utloggning avstängd' : 'Automatisk utloggning uppdaterad');
                }}
                className="px-3 py-2 border border-border/50 rounded-xl text-sm bg-white"
                data-testid="auto-logout-select"
              >
                <option value={15}>Efter 15 minuter</option>
                <option value={30}>Efter 30 minuter</option>
                <option value={60}>Efter 1 timme</option>
                <option value={1440}>Efter 24 timmar</option>
                <option value={0}>Av (aldrig)</option>
              </select>
            </div>
          </div>

          {/* Sessions Section */}
          <div className="pt-4 border-t border-border/30 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Aktiva sessioner</p>
                <p className="text-sm text-muted-foreground">
                  Se och hantera enheter som är inloggade
                </p>
              </div>
              <Button 
                variant="outline"
                onClick={loadSessions}
                disabled={loadingSessions}
                className="rounded-full"
                data-testid="view-sessions-btn"
              >
                {loadingSessions ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
                Visa sessioner
              </Button>
            </div>
            
            {/* Sessions List */}
            {showSessions && sessions.length > 0 && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{sessions.length} aktiva sessioner</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={revokeAllSessions}
                    className="text-destructive text-xs"
                  >
                    Logga ut alla andra
                  </Button>
                </div>
                {sessions.map((session) => (
                  <div 
                    key={session.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${session.is_current ? 'bg-green-50 border border-green-200' : 'bg-secondary/30'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium flex items-center gap-2">
                          {session.is_current && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Denna enhet</span>}
                          IP: {session.ip_address}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Senast aktiv: {new Date(session.last_active).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                    </div>
                    {!session.is_current && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => revokeSession(session.id)}
                        className="text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Export data (GDPR) */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Download className="w-5 h-5 text-teal-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.dataPrivacy}</h3>
          </div>
          <p className="text-muted-foreground">
            Ladda ner all din data (konto, dagboksinlägg, betalhistorik). Detta är din rätt enligt GDPR.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline"
              onClick={handleExportData}
              disabled={exporting}
              className="rounded-full"
              data-testid="export-data-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              {c.exportJson}
            </Button>
            <Button 
              variant="outline"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="rounded-full"
              data-testid="export-pdf-btn"
            >
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              {c.exportPdf}
            </Button>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-card rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.32s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center">
              {darkMode ? <Moon className="w-5 h-5 text-indigo-500" /> : <Sun className="w-5 h-5 text-indigo-500" />}
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.appearance}</h3>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{c.darkMode}</p>
              <p className="text-sm text-muted-foreground">
                {c.darkModeDesc}
              </p>
            </div>
            <Switch
              checked={darkMode}
              onCheckedChange={handleToggleDarkMode}
              data-testid="dark-mode-toggle"
            />
          </div>
        </div>

        {/* Dashboard Favorites */}
        <div className="bg-white dark:bg-card rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.34s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold">Favoriter på Dashboard</h3>
              <p className="text-sm text-muted-foreground">Välj verktyg att visa på din startsida</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {FAVORITE_TOOLS.map(tool => (
              <button
                key={tool.id}
                onClick={() => handleToggleFavorite(tool.id)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                  userFavorites.includes(tool.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-border/30 hover:border-primary/30'
                }`}
                data-testid={`fav-toggle-${tool.id}`}
              >
                <tool.icon className={`w-4 h-4 ${userFavorites.includes(tool.id) ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="text-sm font-medium">{tool.label}</span>
                {userFavorites.includes(tool.id) && <Check className="w-4 h-4 text-primary ml-auto" />}
              </button>
            ))}
          </div>
        </div>

        {/* Privacy & Terms links */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.35s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Juridisk information</h3>
          </div>
          <div className="flex flex-col gap-3">
            <Link 
              to="/privacy" 
              className="flex items-center gap-3 p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
            >
              <FileText className="w-5 h-5 text-muted-foreground" />
              <span>{c.privacyPolicy}</span>
            </Link>
            <Link 
              to="/terms" 
              className="flex items-center gap-3 p-4 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
            >
              <FileText className="w-5 h-5 text-muted-foreground" />
              <span>{c.termsOfService}</span>
            </Link>
          </div>
        </div>

        {/* Logout */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <LogOut className="w-5 h-5 text-orange-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">{c.logout}</h3>
          </div>
          <p className="text-muted-foreground">
            Logga ut från ditt konto på denna enhet.
          </p>
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="rounded-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {c.logout}
          </Button>
        </div>

        {/* Admin Panel - Only show for admin users */}
        {user?.is_admin && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl p-8 space-y-6 animate-fade-in border border-yellow-200 shadow-soft" style={{ animationDelay: '0.45s' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-yellow-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-yellow-800">Admin Panel</h3>
            </div>
            <p className="text-yellow-700">
              Se statistik, användare och intäkter.
            </p>
            <Button 
              onClick={() => navigate('/admin')}
              className="rounded-full bg-yellow-600 hover:bg-yellow-700"
            >
              <Crown className="w-4 h-4 mr-2" />
              Öppna Admin Panel
            </Button>
          </div>
        )}

        {/* Delete account */}
        <div className="bg-white rounded-2xl p-8 space-y-6 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-destructive" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-destructive">{c.deleteAccount}</h3>
          </div>
          <p className="text-muted-foreground">
            När du raderar ditt konto tas all din data bort permanent, inklusive dagboksinlägg och reflektioner. Detta kan inte ångras. Vi rekommenderar att du laddar ner din data först.
          </p>
          
          {!showDeleteConfirm ? (
            <Button 
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {c.deleteAccount}
            </Button>
          ) : (
            <div className="bg-destructive/10 rounded-xl p-6 space-y-4">
              <p className="font-medium text-destructive">
                {c.deleteConfirmText}
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="rounded-full"
                  data-testid="cancel-delete-btn"
                >
                  {c.cancel}
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="rounded-full"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {c.deleteForever}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* App Info */}
        <div className="bg-white rounded-2xl p-8 space-y-4 animate-fade-in border border-border/30 shadow-soft" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
              <Info className="w-5 h-5 text-gray-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold">Om appen</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center py-2 border-b border-border/30">
              <span className="text-muted-foreground">Version</span>
              <span className="font-medium" data-testid="app-version">{APP_VERSION}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border/30">
              <span className="text-muted-foreground">Senast uppdaterad</span>
              <span className="font-medium">2026-02-18</span>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              Livon uppdateras automatiskt. Om du har problem, prova att stänga och öppna appen igen, 
              eller ladda om sidan i webbläsaren.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if ('serviceWorker' in navigator) {
                  navigator.serviceWorker.ready.then(registration => {
                    registration.update();
                    toast.success('Söker efter uppdateringar...');
                  });
                } else {
                  window.location.reload();
                }
              }}
              className="rounded-full text-xs mt-2"
              data-testid="check-updates-btn"
            >
              Sök efter uppdateringar
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
